/** Parse a device-mappings CSV for bulk device upload. Schema-less: whatever
 *  columns the live DynamoDB table uses, the CSV header drives the row shape.
 *  A "name" column (device ID; "deviceId"/"device_id" also accepted) is required. */

import type { DeviceMappingRow } from './deviceMappingRows';

// Known column names, used only to normalize case-insensitive CSV headers to the
// exact casing the table already uses (e.g. "devicerole" -> "deviceRole").
const KNOWN_COLUMNS = [
  'name',
  'deviceRole',
  'pairName',
  'macFront',
  'macRear',
  'athleteId',
  'athleteNumber',
  'eventId',
  'raceId',
  'draftingThresholdSeconds',
  'description',
  'desc',
  'simId',
  'sim_id',
  'sim',
  'iccid',
] as const;

const KNOWN_COLUMN_BY_LOWER = new Map(KNOWN_COLUMNS.map((c) => [c.toLowerCase(), c]));

function stripBom(s: string): string {
  if (s.charCodeAt(0) === 0xfeff) return s.slice(1);
  return s;
}

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      const next = line[i + 1];
      if (inQuotes && next === '"') {
        cur += '"';
        i++;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }
    if (ch === ',' && !inQuotes) {
      out.push(cur);
      cur = '';
      continue;
    }
    cur += ch;
  }
  out.push(cur);
  return out;
}

function parseCsvGrid(text: string): string[][] {
  const lines = stripBom(text).replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const rows: string[][] = [];
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) continue;
    rows.push(parseCsvLine(line));
  }
  return rows;
}

function normalizeHeaderCell(cell: string): string {
  const trimmed = cell.trim();
  const lower = trimmed.toLowerCase();
  if (lower === 'deviceid' || lower === 'device_id') return 'name';
  return KNOWN_COLUMN_BY_LOWER.get(lower) ?? trimmed;
}

export type ParseDeviceMappingsCsvResult = {
  rows: DeviceMappingRow[];
  columns: string[];
  error?: string;
};

/** Parse CSV text into device mapping rows. First row is treated as the header. */
export function parseDeviceMappingsCsv(text: string): ParseDeviceMappingsCsvResult {
  const grid = parseCsvGrid(text);
  if (grid.length === 0) {
    return { rows: [], columns: [], error: 'CSV is empty' };
  }

  const header = grid[0].map(normalizeHeaderCell).filter((h) => h.length > 0);
  if (header.length === 0) {
    return { rows: [], columns: [], error: 'CSV header row is empty' };
  }
  if (!header.includes('name')) {
    return {
      rows: [],
      columns: [],
      error: 'CSV must include a "name" (or "deviceId") column with the device ID',
    };
  }

  const nameIdx = header.indexOf('name');
  const rows: DeviceMappingRow[] = [];
  const firstLineByName = new Map<string, number>();

  for (let r = 1; r < grid.length; r++) {
    const lineNo = r + 1;
    const cols = grid[r] ?? [];
    if (cols.every((c) => !String(c ?? '').trim())) continue;

    const deviceId = String(cols[nameIdx] ?? '').trim();
    if (!deviceId) {
      return { rows: [], columns: [], error: `Line ${lineNo}: missing device ID (name column)` };
    }

    const key = deviceId.toLowerCase();
    if (firstLineByName.has(key)) {
      const firstLine = firstLineByName.get(key)!;
      return {
        rows: [],
        columns: [],
        error: `Duplicate device ID "${deviceId}" on lines ${firstLine} and ${lineNo}`,
      };
    }
    firstLineByName.set(key, lineNo);

    const row: DeviceMappingRow = {};
    header.forEach((col, i) => {
      row[col] = String(cols[i] ?? '').trim();
    });
    row.name = deviceId;
    rows.push(row);
  }

  if (rows.length === 0) {
    return { rows: [], columns: [], error: 'No data rows found in CSV' };
  }

  return { rows, columns: header };
}
